import { apiFetch, ApiError } from "./http";
import type { AuthUser } from "../auth/types";

export type LoginNextStep = "password" | "totp_verify" | "totp_enroll";

export async function startLogin(email: string): Promise<{
  challengeId: string;
  nextStep: LoginNextStep;
  email: string;
}> {
  return apiFetch("/auth/login/start", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function loginWithPassword(challengeId: string, password: string): Promise<AuthUser> {
  const response = await apiFetch<{ user: AuthUser }>("/auth/login/password", {
    method: "POST",
    body: JSON.stringify({ challengeId, password }),
  });
  return response.user;
}

export async function startTotpEnrollment(challengeId: string): Promise<{
  qrDataUrl: string;
  manualKey: string;
  email: string;
}> {
  return apiFetch("/auth/totp/enroll/start", {
    method: "POST",
    body: JSON.stringify({ challengeId }),
  });
}

export async function verifyTotpEnrollment(
  challengeId: string,
  code: string,
): Promise<{ user: AuthUser; recoveryCodes: string[] }> {
  return apiFetch("/auth/totp/enroll/verify", {
    method: "POST",
    body: JSON.stringify({ challengeId, code }),
  });
}

export async function loginWithTotp(challengeId: string, code: string): Promise<AuthUser> {
  const response = await apiFetch<{ user: AuthUser }>("/auth/login/totp", {
    method: "POST",
    body: JSON.stringify({ challengeId, code }),
  });
  return response.user;
}

export async function loginWithRecovery(challengeId: string, recoveryCode: string): Promise<AuthUser> {
  const response = await apiFetch<{ user: AuthUser }>("/auth/login/recovery", {
    method: "POST",
    body: JSON.stringify({ challengeId, recoveryCode }),
  });
  return response.user;
}

export async function fetchCurrentUser(): Promise<AuthUser | null> {
  try {
    const response = await apiFetch<{ user: AuthUser }>("/auth/me");
    return response.user;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export async function login(email: string, password: string): Promise<AuthUser> {
  const response = await apiFetch<{ user: AuthUser }>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  return response.user;
}

export async function logout(): Promise<void> {
  await apiFetch("/auth/logout", { method: "POST", body: JSON.stringify({}) });
}

export async function logoutAllSessions(): Promise<void> {
  await apiFetch("/auth/logout-all", { method: "POST", body: JSON.stringify({}) });
}
